import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Save, Palette, Globe, Mail } from "lucide-react";

export function WhiteLabelSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ["white-label-settings"],
    queryFn: async () => {
      const { data: institutionSettings } = await supabase
        .from("institution_settings" as any)
        .select("id")
        .single();

      if (!institutionSettings) throw new Error("No institution found");

      const { data, error } = await supabase
        .from("white_label_settings" as any)
        .select("*")
        .eq("institution_id", (institutionSettings as any).id)
        .single();

      if (error && error.code !== "PGRST116") throw error;

      return (data || {
        institution_id: (institutionSettings as any).id,
        primary_color: "#4F46E5",
        secondary_color: "#10B981",
        accent_color: "#F59E0B",
      }) as any;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (updates: any) => {
      const { error } = await supabase
        .from("white_label_settings" as any)
        .upsert(updates);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["white-label-settings"] });
      toast({
        title: "Success",
        description: "White label settings saved successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSaving(true);

    const formData = new FormData(e.currentTarget);
    const updates = {
      institution_id: settings.institution_id,
      custom_domain: formData.get("custom_domain") as string,
      logo_url: formData.get("logo_url") as string,
      favicon_url: formData.get("favicon_url") as string,
      primary_color: formData.get("primary_color") as string,
      secondary_color: formData.get("secondary_color") as string,
      accent_color: formData.get("accent_color") as string,
      email_from_name: formData.get("email_from_name") as string,
      email_from_address: formData.get("email_from_address") as string,
      support_email: formData.get("support_email") as string,
      support_phone: formData.get("support_phone") as string,
      custom_css: formData.get("custom_css") as string,
    };

    await saveMutation.mutateAsync(updates);
    setIsSaving(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card className="border-2 hover:border-primary/20 transition-colors">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-primary" />
          <CardTitle>White Label Branding</CardTitle>
        </div>
        <CardDescription>
          Customize the appearance and branding for your institution
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Domain & Identity</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="custom_domain">Custom Domain</Label>
                <Input
                  id="custom_domain"
                  name="custom_domain"
                  type="text"
                  defaultValue={settings?.custom_domain || ""}
                  placeholder="compliance.youruniversity.edu"
                />
                <p className="text-xs text-muted-foreground">
                  Configure DNS CNAME to point to our servers
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="logo_url">Logo URL</Label>
                <Input
                  id="logo_url"
                  name="logo_url"
                  type="url"
                  defaultValue={settings?.logo_url || ""}
                  placeholder="https://cdn.example.com/logo.png"
                />
              </div>

              <div className="space-y-2 col-span-2">
                <Label htmlFor="favicon_url">Favicon URL</Label>
                <Input
                  id="favicon_url"
                  name="favicon_url"
                  type="url"
                  defaultValue={settings?.favicon_url || ""}
                  placeholder="https://cdn.example.com/favicon.ico"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Palette className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Color Scheme</h3>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primary_color">Primary Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="primary_color"
                    name="primary_color"
                    type="color"
                    defaultValue={settings?.primary_color || "#4F46E5"}
                    className="w-20"
                  />
                  <Input
                    type="text"
                    defaultValue={settings?.primary_color || "#4F46E5"}
                    placeholder="#4F46E5"
                    className="flex-1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="secondary_color">Secondary Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="secondary_color"
                    name="secondary_color"
                    type="color"
                    defaultValue={settings?.secondary_color || "#10B981"}
                    className="w-20"
                  />
                  <Input
                    type="text"
                    defaultValue={settings?.secondary_color || "#10B981"}
                    placeholder="#10B981"
                    className="flex-1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="accent_color">Accent Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="accent_color"
                    name="accent_color"
                    type="color"
                    defaultValue={settings?.accent_color || "#F59E0B"}
                    className="w-20"
                  />
                  <Input
                    type="text"
                    defaultValue={settings?.accent_color || "#F59E0B"}
                    placeholder="#F59E0B"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Email & Support</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email_from_name">Email From Name</Label>
                <Input
                  id="email_from_name"
                  name="email_from_name"
                  type="text"
                  defaultValue={settings?.email_from_name || ""}
                  placeholder="University Compliance Office"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email_from_address">Email From Address</Label>
                <Input
                  id="email_from_address"
                  name="email_from_address"
                  type="email"
                  defaultValue={settings?.email_from_address || ""}
                  placeholder="compliance@university.edu"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="support_email">Support Email</Label>
                <Input
                  id="support_email"
                  name="support_email"
                  type="email"
                  defaultValue={settings?.support_email || ""}
                  placeholder="support@university.edu"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="support_phone">Support Phone</Label>
                <Input
                  id="support_phone"
                  name="support_phone"
                  type="tel"
                  defaultValue={settings?.support_phone || ""}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="custom_css">Custom CSS (Advanced)</Label>
            <Textarea
              id="custom_css"
              name="custom_css"
              defaultValue={settings?.custom_css || ""}
              placeholder="/* Add custom CSS here */"
              rows={6}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Add custom CSS to override default styles. Use with caution.
            </p>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save White Label Settings
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
